import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { ThreadSummary } from '@core/tellent-assistant-types';
import { TellentMobileLongPressDirective } from '@core/tellent-mobile-ui';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  DividerComponent,
  DropdownComponent,
  DropdownItemComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

import { ThreadMenuAction, ThreadMenuItem, threadMenuGroups } from './thread-menu';

@Component({
  selector: 'tellent-assistant-thread-drawer',
  standalone: true,
  imports: [
    ButtonComponent,
    ConnectedOverlayDirective,
    DividerComponent,
    DropdownComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    TellentMobileLongPressDirective,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { role: 'complementary', '[class.embedded]': 'embedded()' },
  templateUrl: './tellent-assistant-thread-drawer.component.html',
  styleUrl: './tellent-assistant-thread-drawer.component.less',
})
export class TellentAssistantThreadDrawerComponent {
  private readonly i18n = inject(I18nService);

  protected readonly defaultTitle = this.i18n.translate(
    'tlc.tellent_assistant.thread.default_title',
  );
  protected readonly menuOpenForThread = signal<string | null>(null);
  protected readonly menuPositions = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];
  protected readonly menuGroups = threadMenuGroups;

  public readonly threads = input.required<readonly ThreadSummary[]>();
  public readonly activeId = input<string | null>(null);
  public readonly isDeleting = input<(threadId: string) => boolean>(() => false);
  // Hosted in the mobile sheet: the hover-revealed kebab menu is replaced by a
  // long press on the thread row (surfaced through the threadOptions output),
  // rows are denser, and the Tellent Admin deep link is dropped.
  public readonly embedded = input(false);
  public readonly newThread = output<void>();
  public readonly selectThread = output<string>();
  public readonly menuAction = output<ThreadMenuAction>();
  public readonly threadOptions = output<string>();
  public readonly helpCenter = output<void>();
  public readonly intelligenceSettings = output<void>();

  protected onKebabClick(threadId: string, _origin: OverlayOriginDirective, event: Event): void {
    event.stopPropagation();
    this.menuOpenForThread.set(threadId);
  }

  protected onMenuItemClick(item: ThreadMenuItem, thread: ThreadSummary): void {
    this.menuOpenForThread.set(null);
    this.menuAction.emit({ id: item.id, thread });
  }
}
